import React, { useMemo } from 'react';
import styles from './EndGameHeaderInfo.module.scss';
import { useUIGameEndContext } from '../../../../contexts/UIGameEndContext.tsx';
import { T } from '@colonist/ui';
import clsx from 'clsx';
import { FADE_IN_ANIMATION_DELAYS } from '../../constants.ts';
import { TimeUtils } from '@colonist/utils';
import { ModeSettingEnum } from '@colonist/types';
import IconLogo from '@static/images/logo.png';
import { FadeInContainer } from '../../../../common/FadeInContainer/index.ts';
import { useGameStore } from '../../../../store/GameStoreContext.ts';
import { usePlayerValidators } from '../../../../store/selectors/usePlayerValidators.ts';
export function EndGameHeaderInfo({ disableAnimation }) {
    const modeSetting = useGameStore((state)=>state.gameSettings.modeSetting);
    const { gameEndData, isMyColorWinner, currentPlayerEndgameState, onClickHome } = useUIGameEndContext();
    const { playerValidators } = usePlayerValidators();
    const isMyColorSpectator = useGameStore((state)=>state.isMyColorSpectator);
    const isBeginnerMode = modeSetting == ModeSettingEnum.BeginnerModeClassic4P;
    const isResignation = Object.values(gameEndData.endGameState.players).some((player)=>player.hasResigned);
    const showResignationForWinner = isMyColorWinner && !isMyColorSpectator && isResignation;
    const totalPlayers = playerValidators.length;
    const endGameText = useMemo(()=>{
        if (isBeginnerMode && !isMyColorWinner) {
            const beginnerModeLostText = {
                key: 'strings:game.beginnerMode.opponentWon'
            };
            return beginnerModeLostText;
        }
        if (isMyColorSpectator) return {
            key: 'strings:game.endGame.rank.spectator'
        };
        const playerRank = currentPlayerEndgameState === null || currentPlayerEndgameState === void 0 ? void 0 : currentPlayerEndgameState.rank;
        if (playerRank == null) return {
            key: 'strings:game.endGame.rank.wellPlayed'
        };
        const averageRank = Math.min(3, Math.floor(totalPlayers / 2));
        const maxRankToShow = totalPlayers <= 3 ? 1 : averageRank;
        const shouldShowRank = playerRank <= maxRankToShow;
        if (!shouldShowRank) {
            return {
                key: 'strings:game.endGame.rank.wellPlayed'
            };
        }
        switch(playerRank){
            case 1:
                return {
                    key: 'strings:game.endGame.rank.1st'
                };
            case 2:
                return {
                    key: 'strings:game.endGame.rank.2nd'
                };
            case 3:
                return {
                    key: 'strings:game.endGame.rank.3rd'
                };
            default:
                return {
                    key: 'strings:game.endGame.rank.wellPlayed'
                };
        }
    }, [
        currentPlayerEndgameState,
        isMyColorWinner,
        isBeginnerMode,
        isMyColorSpectator,
        totalPlayers
    ]);
    const timeTurnsText = useMemo(()=>{
        if (isBeginnerMode && !isMyColorWinner) {
            const beginnerModeLostText = {
                key: 'strings:game.beginnerMode.lost'
            };
            return beginnerModeLostText;
        }
        const text = {
            key: 'strings:game.endGame.generalStats',
            options: {
                timeString: TimeUtils.formatSecondsToElapsedString(gameEndData.endGameState.gameDurationInMS / 1000),
                turnCount: String(gameEndData.endGameState.totalTurnCount)
            }
        };
        return text;
    }, [
        gameEndData,
        isMyColorWinner,
        isBeginnerMode
    ]);
    return /*#__PURE__*/ React.createElement(FadeInContainer, {
        animationDelayMs: FADE_IN_ANIMATION_DELAYS.logo,
        animationDurationMs: FADE_IN_ANIMATION_DELAYS.transitionDuration,
        className: clsx(styles.logoContainer, {
            [styles.hasWinReason]: showResignationForWinner
        }),
        disableAnimation: disableAnimation
    }, /*#__PURE__*/ React.createElement("img", {
        className: styles.logo,
        onClick: onClickHome,
        src: IconLogo
    }), /*#__PURE__*/ React.createElement("div", {
        className: styles.heading
    }, /*#__PURE__*/ React.createElement(T, {
        id: endGameText
    })), showResignationForWinner && /*#__PURE__*/ React.createElement("div", {
        className: styles.winReason
    }, /*#__PURE__*/ React.createElement(T, {
        id: "strings:game.endGame.resignation"
    })), /*#__PURE__*/ React.createElement("div", {
        className: clsx(styles.timeTurns)
    }, /*#__PURE__*/ React.createElement(T, {
        id: timeTurnsText
    })));
}
